<template>
  <div>
    <BaseNav
      id="main-navbar"
      type="dark"
      container-classes="container px-3"
      menu-classes="bg-dark"
      class="navbar-horizontal"
      expand="lg"
    >
      <div slot="brand" class="navbar-wrapper d-inline-flex align-items-center">
        <b-navbar-brand
          :to="{
            name: isUserRole('Guest') ? 'Home' : 'Dashboard'
          }"
          class="my-0"
        >
          <img
            src="@assets/img/logo.png"
            alt="LimeIQ"
            height="5px !important"
          />
        </b-navbar-brand>
      </div>

      <template v-if="!isUserRole('Superuser')">
        <span slot="toggle-button"></span>
      </template>

      <b-navbar-nav v-if="isUserRole('Superuser')" class="mr-auto">
        <b-nav-item
          :to="{ name: 'Companies' }"
          :active="superuserNavActive.companies"
        >
          <span class="nav-link-inner--text">Companies</span>
        </b-nav-item>
        <b-nav-item
          :to="{ name: 'Users' }"
          :active="superuserNavActive.users"
        >
          <span class="nav-link-inner--text">Users</span>
        </b-nav-item>
        <b-nav-item
          :to="{ name: 'Reports' }"
          :active="superuserNavActive.reports"
        >
          <span class="nav-link-inner--text">Reports</span>
        </b-nav-item>
        <b-nav-item
          :active="superuserNavActive.export"
          @click.prevent="navigateToExporter"
        >
          <span class="nav-link-inner--text">Export</span>
        </b-nav-item>
        <b-nav-item
          :to="{ name: 'Invoicing' }"
          :active="superuserNavActive.invoicing"
        >
          <span class="nav-link-inner--text">Invoicing</span>
        </b-nav-item>
      </b-navbar-nav>

      <template #non-collapse>
        <b-navbar-nav class="report-actions align-items-center ml-auto">
          <NewBDXIconNavItem class="mr-1" />

          <CompanyListDropdown v-if="['ManageBDX'].includes(route.name)" />

          <ReportsDropdown
            v-if="currentUser.company_assigned"
            class="mr-2 mr-lg-0"
            :active-report="activeReport"
            :reports="navbarReportsList"
          />

          <template v-if="!isUserRole('Superuser')">
            <b-nav-item @click.prevent="navigateToExporter">
              <span class="nav-link-inner--text btn text-white m-0 border"
                >Export</span
              >
            </b-nav-item>
          </template>

          <template v-if="isUserRole('Superuser')">
            <!-- Keeps the BDX list one click away, as it was before this became a
                 menu, while hovering reveals the other BDX tools. Slip Share is
                 superuser-only, so only superusers get the dropdown at all. -->
            <b-nav-item
              @click.prevent="handleDedupeRequest"
              link-classes="px-2"
            >
              <b-icon-arrow-clockwise
                :animation="isDedupeRequestBusy ? 'spin' : ''"
              ></b-icon-arrow-clockwise>
            </b-nav-item>
            <b-nav-item>
              <base-dropdown
                menu-on-right
                class="nav-item"
                tag="li"
                title-tag="a"
                title-classes="nav-link"
              >
                <span
                  class="nav-link px-0"
                  slot="title-container"
                  title="BDX tools"
                  @click="navigateToBDXList"
                >
                  <b-icon-list-check></b-icon-list-check>
                </span>

                <b-dropdown-item :to="{ name: 'ManageBDX' }">
                  <b-icon-list-check class="mr-2"></b-icon-list-check>
                  <span>BDX List</span>
                </b-dropdown-item>
                <b-dropdown-item :to="{ name: 'SlipShareService' }">
                  <b-icon-percent class="mr-2"></b-icon-percent>
                  <span>Slip Share</span>
                </b-dropdown-item>
              </base-dropdown>
            </b-nav-item>
          </template>
          <template v-else>
            <!-- Non-superusers have no BDX tools menu - the icon links straight
                 to the BDX list. -->
            <b-nav-item
              link-classes="px-2"
              title="BDX List"
              @click.prevent="navigateToBDXList"
            >
              <b-icon-list-check></b-icon-list-check>
            </b-nav-item>
          </template>

          <b-nav-item
            v-if="hasFCPAccess"
            link-classes="px-2"
            :to="{ name: 'Reconciliation' }"
          >
            <span>REC</span>
          </b-nav-item>

          <template v-if="['Dashboard', 'ReportView'].includes(route.name)">
            <b-nav-item
              v-if="$user.get().is_superuser"
              :disabled="reportLoading"
              link-classes="px-2"
            >
              <ReportAsCompanyDropdown :active-report-id="activeReport" />
            </b-nav-item>

            <template v-if="reportValid">
              <b-nav-item
                :disabled="reportLoading"
                link-classes="px-2"
                @click.prevent="() => $emitter.emit('fullscreen-report', true)"
              >
                <b-icon-fullscreen></b-icon-fullscreen>
              </b-nav-item>
              <b-nav-item
                :disabled="reportLoading"
                link-classes="px-2"
                @click.prevent="
                  () =>
                    $emitter.emit('toggle-modal', {
                      key: 'share-report',
                      active: true
                    })
                "
              >
                <b-icon-share></b-icon-share>
              </b-nav-item>
            </template>
          </template>

          <b-nav-item>
            <base-dropdown
              menu-on-right
              class="nav-item"
              tag="li"
              title-tag="a"
              title-classes="nav-link"
            >
              <span class="nav-link px-0" slot="title-container">
                <b-icon-plus-circle></b-icon-plus-circle>
              </span>

              <b-dropdown-item @click.prevent="() => handleShowUploader('BDX')">
                <span>BDX</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="hasFCPAccess && (isUserRole('Superuser') || isUnderwriter)"
                @click="() => handleShowUploader('FCP')"
              >
                <span>FCP</span>
              </b-dropdown-item>
            </base-dropdown>
          </b-nav-item>

          <!-- <b-nav-item v-if="isUserRole('Superuser') || currentUser.company && currentUser.company.role.role == 'TPA'"
              link-classes="px-2"
              :to="{ name: 'CashCallDetails' }"
              title="Cash Call">
              <b-icon-currency-dollar></b-icon-currency-dollar>
          </b-nav-item> -->

          <b-nav-item
            v-if="isSuperUser && alerts.needs_attention"
            link-classes="px-2"
            :to="{ name: 'Invoicing', query: { tab: 'generate-invoices', filter: 'needs-attention' } }"
            title="Invoices need attention"
            @click="requestFilter('needs-attention')"
          >
            <b-icon-exclamation-triangle-fill class="text-warning"></b-icon-exclamation-triangle-fill>
          </b-nav-item>

          <b-nav-item
            v-if="isUserRole('Superuser') || canAccessCashCall"
            link-classes="px-2"
            :to="{ name: 'CashCallDetails' }"
            title="Cash Call"
          >
            <b-icon-currency-dollar></b-icon-currency-dollar>
          </b-nav-item>


          <!-- <b-nav-item v-if="isUserRole('Superuser')">
            <base-dropdown
              menu-on-right
              class="nav-item"
              tag="li"
              title-tag="a"
              title-classes="nav-link"
            >
              <span class="nav-link px-0" slot="title-container">
                <b-icon-currency-dollar></b-icon-currency-dollar>
              </span>

              <b-dropdown-item @click="() => (showCashCallModal = !showCashCallModal)">
                <span>Create Cash Call</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="hasFCPAccess"
                @click="() => handleShowCashCallStatus()"
              >
                <span>Cash Call Status</span>
              </b-dropdown-item>
            </base-dropdown>
          </b-nav-item> -->

          <b-nav-item
            v-if="isUserRole('Superuser')"
            href="https://forms.clickup.com/8461271/f/826yq-9831/NHST25KGCY8X3K9ZI6"
            target="_blank"
          >
            <b-icon-question-circle></b-icon-question-circle>
          </b-nav-item>
        </b-navbar-nav>

        <b-navbar-nav class="align-items-center">
          <b-nav-item v-if="isUserRole('Guest')" :to="{ name: 'Home' }">
            <span class="nav-link-inner--text">Login</span>
          </b-nav-item>

          <b-nav-item v-else class="ml-2 mx-lg-0">
            <base-dropdown
              ref="profileDropdownRef"
              menu-on-right
              class="nav-item"
              tag="li"
              title-tag="a"
              title-classes="nav-link"
            >
              <span class="nav-link px-0" slot="title-container">
                <b-media no-body class="align-items-center">
                  <span class="avatar avatar-xs rounded-circle">
                    <Avatar
                      @click.native="
                        () =>
                          (profileDropdownRef.isOpen =
                            !profileDropdownRef.isOpen)
                      "
                      :src="currentUser.profile_photo"
                      :editable="false"
                      initials-only
                      size="28px"
                    />
                  </span>
                </b-media>
              </span>

              <b-dropdown-item :to="{ name: 'ExportLog' }">
                <span>Export Log</span>
              </b-dropdown-item>
              <b-dropdown-item v-if="hasFCPAccess" :to="{ name: 'FCPLogList' }">
                <span>FCP Logs</span>
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'OrphanClaims' }">
                <span>Orphan Claims</span>
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'UserProfile' }">
                <span>My Profile</span>
              </b-dropdown-item>
              <template v-if="isUserRole('Company')">
                <b-dropdown-item :to="{ name: 'Users' }">
                  <span>Manage Users</span>
                </b-dropdown-item>
                <b-dropdown-item :to="{ name: 'Reports' }">
                  <span>Manage Reports</span>
                </b-dropdown-item>
              </template>
              <b-dropdown-item :to="{ name: 'UserSettings' }">
                <span>Settings</span>
              </b-dropdown-item>
              <b-dropdown-item @click.prevent="handleLogout">
                <span>Logout</span>
              </b-dropdown-item>
            </base-dropdown>
          </b-nav-item>
        </b-navbar-nav>
      </template>
    </BaseNav>

    <div v-if="showBDXUploader" class="py-4 px-3">
      <a
        @click.prevent="() => (showBDXUploader = false)"
        class="float-right small"
        href
        >Close</a
      >
      <div class="mb-2 clearfix"></div>
      <BDXUploader
        class="mb-3"
        :input-attrs="{ multiple: true, accept: '.xls,.xlsx' }"
      />
    </div>

    <div v-if="hasFCPAccess && (isUserRole('Superuser') || isUnderwriter) && showFCPUploader" class="py-4 px-3">
      <a
        @click.prevent="() => (showFCPUploader = false)"
        class="float-right small"
        href
        >Close</a
      >
      <div class="mb-2 clearfix"></div>
      <FCPUploader
        class="mb-3"
        :input-attrs="{ multiple: true, accept: '.xls,.xlsx' }"
      />
    </div>

    <CashCallCreateModal  v-model="showCashCallModal" is-modal />
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  getCurrentInstance,
  computed,
  ref,
  watch
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'
import moment from 'moment'

import { AuthenticationService, Lincoln, ReportsService } from '@services'
import { useCurrentUser, useExport, useInvoiceAlerts } from '@composables'
import { useAuth } from '@composables/auth'
import { useReport } from '@composables/report'

import { BaseNav } from '@/components/base'
import { CompanyListDropdown } from '@components/company'
import { BDXUploader, NewBDXIconNavItem } from '@components/bdx'
import Avatar from '@components/Avatar'
import ReportAsCompanyDropdown from '@components/ReportAsCompanyDropdown'
import ReportsDropdown from '@components/ReportsDropdown'
import FCPUploader from '@components/FCP/FCPUploader'
import CashCallCreateModal from '@components/CashCall/CashCallCreateModal.vue'


export default defineComponent({
  name: 'AppNavbar',
  components: {
    BaseNav,
    NewBDXIconNavItem,
    Avatar,
    CompanyListDropdown,
    ReportsDropdown,
    ReportAsCompanyDropdown,
    CashCallCreateModal,
    BDXUploader,
    FCPUploader
  },
  props: {
    type: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'light'].includes(value)
    }
  },
  setup() {
    const root = getCurrentInstance()

    const { useGetters, useActions } = createVuexHelpers()

    const { reportValid } = useGetters(['reportValid'])
    const {
      setAccessToken,
      setRefreshToken,

      setUser
    } = useActions(['setAccessToken', 'setRefreshToken', 'setUser'])

    const route = useRoute()
    const router = useRouter()

    const { isSuperUser, hasFCPAccess, canAccessCashCall, canCreateCashCall } = useCurrentUser()
    const { currentUser, isUserRole } = useAuth()
    const { decodeReportHash } = useReport()
    const { isExportEnabled } = useExport()
    const { alerts, requestFilter } = useInvoiceAlerts()

    const reportLoading = ref(true)
    const showCashCallModal = ref(false)
    const showBDXUploader = ref(false)
    const showFCPUploader = ref(false)
    const isDedupeRequestBusy = ref(false)
    const companiesList = ref([])
    const profileDropdownRef = ref()
    // Fetched once here and shared (via the `reports` prop) with ReportsDropdown,
    // instead of it independently calling the same /assigned/report/ endpoint on mount.
    const navbarReportsList = ref([])

    const isUnderwriter = computed(() => {
      return currentUser.value &&
        currentUser.value.company &&
        currentUser.value.company.role &&
        currentUser.value.company.role.role === 'Underwriter'
    })

    const isExportUploaderEnabled = computed(
      () => 'True' == process.env.VUE_APP_ENABLE_EXPORT_UPLOADER
    )

    const navigateToExporter = () => {
      if (isExportEnabled.value) router.push({ name: 'Export' })
      else
        Vue.prototype.$toasted.show(
          'Export feature is in maintenance and will be available shortly.'
        )
    }

    // The dropdown trigger used to be a plain link to the BDX list. Pushing the
    // same route twice is a rejected navigation in vue-router, so skip it when
    // we are already there.
    const navigateToBDXList = () => {
      if (route.name !== 'ManageBDX') router.push({ name: 'ManageBDX' })
    }

    const routeName = computed(
      () => route.name.charAt(0).toUpperCase() + route.name.slice(1)
    )

    const superuserNavActive = computed(() => {
      return {
        companies: route.name === 'Companies',
        users: route.name === 'Users',
        reports: route.name === 'Reports',
        export: route.name === 'Export',
        invoicing: route.name === 'Invoicing'
      }
    })

    const activeReport = computed(() => {
      if (!['Dashboard', 'ReportView'].includes(route.name)) return null

      if (route.params.report_id || route.params.id) {
        const decodedHash = decodeReportHash(
          route.params.report_id || route.params.id
        )

        return decodedHash.reportID
      } else {
        return root.dashboard_report || currentUser.value.report_id
      }
    })

    const handleShowCashCallStatus = () => {
      alert('This Page is under construction');
    }

    const handleShowUploader = (type) => {
      if ('BDX' === type) {
        showBDXUploader.value = true
        showFCPUploader.value = false
      } else if ('FCP' === type) {
        showFCPUploader.value = true
        showBDXUploader.value = false
      }
    }

    const handleDedupeRequest = async () => {
      const dedupeRequestLastTriggered = localStorage.getItem(
        'dedupeRequestLastTriggered'
      )
      const dedupeRequestMinutesTriggered = dedupeRequestLastTriggered
        ? moment().diff(dedupeRequestLastTriggered, 'minutes')
        : undefined

      if (
        undefined !== dedupeRequestMinutesTriggered &&
        dedupeRequestMinutesTriggered <= 10
      ) {
        Vue.prototype.$toasted.show('You already have a refresh queued.')
      } else {
        localStorage.removeItem('dedupeRequestLastTriggered')

        isDedupeRequestBusy.value = true

        Lincoln.triggerDedupeUpdate(currentUser.value.email).catch((err) =>
          Promise.reject(err)
        )

        localStorage.setItem(
          'dedupeRequestLastTriggered',
          moment().format('YYYY-MM-DD HH:mm:ss')
        )

        setTimeout(() => {
          isDedupeRequestBusy.value = false

          Vue.prototype.$swal({
            title: 'Refresh Triggered',
            html: `
              <p>The process generally takes less than 10 minutes.</p>
              <p class="mb-0">Please check back soon.</p>
            `,
            type: 'success',
            timer: 3000,
            showConfirmButton: false,
            showCancelButton: false,
            customClass: {
              htmlContainer: 'mt-2'
            }
          })
        }, 1000)
      }
    }

    const handleLogout = async () => {
      // SSO logout must end the Entra session too, otherwise the still-active
      // Entra session silently re-authenticates the next login. Redirect to the
      // backend OIDC logout endpoint, which clears the Django session AND ends
      // the Entra session, then returns to the login page. Do this BEFORE
      // /auth/logout/ - that clears the Django session and would skip the Entra
      // logout (OIDCLogoutView only ends the Entra session while authenticated).
      if ('1' === window.localStorage.getItem('limeiq_sso_session')) {
        window.localStorage.removeItem('limeiq_sso_session')
        setAccessToken(null)
        setRefreshToken(null)
        setUser(null)
        window.location.href = `${process.env.VUE_APP_BACKEND_URL}/auth/sso/logout/`
        return
      }

      await AuthenticationService.logout().catch(() => {
        setAccessToken(null)
        setRefreshToken(null)

        setUser(null)
      })

      Vue.prototype.$emitter.emit('logged-out', true)
    }

    // The navbar mounts once and outlives route changes, often before the user's
    // company/member id is in the store — ReportsService.list() then returns
    // false without fetching. Key the fetch on the user instead of on mount so
    // it runs as soon as that data lands (and again if the user changes).
    watch(
      () => {
        const user = currentUser.value
        if (!user || !user.company_assigned) return null
        return [user.id, user.company && user.company.id, user.member_id].join('|')
      },
      async (key) => {
        if (!key) {
          navbarReportsList.value = []
          return
        }

        const rs = await ReportsService.list({ page_size: 1000 }, false)

        if (rs) navbarReportsList.value = rs.data.data.data
      },
      { immediate: true }
    )

    Vue.prototype.$emitter.on(
      'report-loading',
      (payload) => (reportLoading.value = payload)
    )

    Vue.prototype.$emitter.on('files-uploaded', ({ type, success }) => {
      if (!success) return

      setTimeout(() => {
        if ('bdx' === type) showBDXUploader.value = false
        else if ('fcp' === type) showFCPUploader.value = false
      }, 2000)
    })

    return {
      moment,
      route,
      reportValid,
      isSuperUser,
      hasFCPAccess,
      alerts,
      requestFilter,
      currentUser,
      isUserRole,
      isUnderwriter,
      canAccessCashCall, 
      canCreateCashCall,
      reportLoading,
      showCashCallModal,
      showBDXUploader,
      showFCPUploader,
      isDedupeRequestBusy,
      companiesList,
      profileDropdownRef,
      navbarReportsList,
      isExportEnabled,
      isExportUploaderEnabled,
      navigateToExporter,
      navigateToBDXList,
      routeName,
      superuserNavActive,
      activeReport,
      handleShowUploader,
      handleDedupeRequest,
      handleLogout,
      handleShowCashCallStatus
    }
  }
})
</script>
